/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { PaginatedXenialCatalogs } from '../models/paginated-xenial-catalogs';
import { ReportingCategoriesResult } from '../models/reporting-categories-result';
import { StoreSites } from '../models/store-sites';
import { XenialCatalogResponse } from '../models/xenial-catalog-response';
import { xenialCatalogsCatalogEntryIdGet } from '../fn/xenial/xenial-catalogs-catalog-entry-id-get';
import { XenialCatalogsCatalogEntryIdGet$Params } from '../fn/xenial/xenial-catalogs-catalog-entry-id-get';
import { xenialCatalogsCatalogEntryIdPut } from '../fn/xenial/xenial-catalogs-catalog-entry-id-put';
import { XenialCatalogsCatalogEntryIdPut$Params } from '../fn/xenial/xenial-catalogs-catalog-entry-id-put';
import { xenialCatalogsGet } from '../fn/xenial/xenial-catalogs-get';
import { XenialCatalogsGet$Params } from '../fn/xenial/xenial-catalogs-get';
import { xenialReportingCategoriesGet } from '../fn/xenial/xenial-reporting-categories-get';
import { XenialReportingCategoriesGet$Params } from '../fn/xenial/xenial-reporting-categories-get';
import { xenialStoresCompanyIdGet } from '../fn/xenial/xenial-stores-company-id-get';
import { XenialStoresCompanyIdGet$Params } from '../fn/xenial/xenial-stores-company-id-get';

@Injectable({ providedIn: 'root' })
export class XenialService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `xenialReportingCategoriesGet()` */
  static readonly XenialReportingCategoriesGetPath = '/xenial/reporting-categories';

  /**
   * Get reporting Categories from xenial
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `xenialReportingCategoriesGet()` instead.
   *
   * This method doesn't expect any request body.
   */
  xenialReportingCategoriesGet$Response(params: XenialReportingCategoriesGet$Params, context?: HttpContext): Observable<StrictHttpResponse<ReportingCategoriesResult>> {
    return xenialReportingCategoriesGet(this.http, this.rootUrl, params, context);
  }

  /**
   * Get reporting Categories from xenial
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `xenialReportingCategoriesGet$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  xenialReportingCategoriesGet(params: XenialReportingCategoriesGet$Params, context?: HttpContext): Observable<ReportingCategoriesResult> {
    return this.xenialReportingCategoriesGet$Response(params, context).pipe(
      map((r: StrictHttpResponse<ReportingCategoriesResult>): ReportingCategoriesResult => r.body)
    );
  }

  /** Path part for operation `xenialCatalogsCatalogEntryIdGet()` */
  static readonly XenialCatalogsCatalogEntryIdGetPath = '/xenial/catalogs/{catalogEntryId}';

  /**
   * Retrieve a Xenial Catalog Entry
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `xenialCatalogsCatalogEntryIdGet()` instead.
   *
   * This method doesn't expect any request body.
   */
  xenialCatalogsCatalogEntryIdGet$Response(params: XenialCatalogsCatalogEntryIdGet$Params, context?: HttpContext): Observable<StrictHttpResponse<XenialCatalogResponse>> {
    return xenialCatalogsCatalogEntryIdGet(this.http, this.rootUrl, params, context);
  }

  /**
   * Retrieve a Xenial Catalog Entry
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `xenialCatalogsCatalogEntryIdGet$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  xenialCatalogsCatalogEntryIdGet(params: XenialCatalogsCatalogEntryIdGet$Params, context?: HttpContext): Observable<XenialCatalogResponse> {
    return this.xenialCatalogsCatalogEntryIdGet$Response(params, context).pipe(
      map((r: StrictHttpResponse<XenialCatalogResponse>): XenialCatalogResponse => r.body)
    );
  }

  /** Path part for operation `xenialCatalogsCatalogEntryIdPut()` */
  static readonly XenialCatalogsCatalogEntryIdPutPath = '/xenial/catalogs/{catalogEntryId}';

  /**
   * Put Xenial Catalog Entries
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `xenialCatalogsCatalogEntryIdPut()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  xenialCatalogsCatalogEntryIdPut$Response(params: XenialCatalogsCatalogEntryIdPut$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return xenialCatalogsCatalogEntryIdPut(this.http, this.rootUrl, params, context);
  }

  /**
   * Put Xenial Catalog Entries
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `xenialCatalogsCatalogEntryIdPut$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  xenialCatalogsCatalogEntryIdPut(params: XenialCatalogsCatalogEntryIdPut$Params, context?: HttpContext): Observable<void> {
    return this.xenialCatalogsCatalogEntryIdPut$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `xenialCatalogsGet()` */
  static readonly XenialCatalogsGetPath = '/xenial/catalogs';

  /**
   * Get All Xenial Catalog Entries
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `xenialCatalogsGet()` instead.
   *
   * This method doesn't expect any request body.
   */
  xenialCatalogsGet$Response(params: XenialCatalogsGet$Params, context?: HttpContext): Observable<StrictHttpResponse<PaginatedXenialCatalogs>> {
    return xenialCatalogsGet(this.http, this.rootUrl, params, context);
  }

  /**
   * Get All Xenial Catalog Entries
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `xenialCatalogsGet$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  xenialCatalogsGet(params: XenialCatalogsGet$Params, context?: HttpContext): Observable<PaginatedXenialCatalogs> {
    return this.xenialCatalogsGet$Response(params, context).pipe(
      map((r: StrictHttpResponse<PaginatedXenialCatalogs>): PaginatedXenialCatalogs => r.body)
    );
  }

  /** Path part for operation `xenialStoresCompanyIdGet()` */
  static readonly XenialStoresCompanyIdGetPath = '/xenial/stores/{companyId}';

  /**
   * Get store sites from xenial
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `xenialStoresCompanyIdGet()` instead.
   *
   * This method doesn't expect any request body.
   */
  xenialStoresCompanyIdGet$Response(params: XenialStoresCompanyIdGet$Params, context?: HttpContext): Observable<StrictHttpResponse<StoreSites>> {
    return xenialStoresCompanyIdGet(this.http, this.rootUrl, params, context);
  }

  /**
   * Get store sites from xenial
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `xenialStoresCompanyIdGet$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  xenialStoresCompanyIdGet(params: XenialStoresCompanyIdGet$Params, context?: HttpContext): Observable<StoreSites> {
    return this.xenialStoresCompanyIdGet$Response(params, context).pipe(
      map((r: StrictHttpResponse<StoreSites>): StoreSites => r.body)
    );
  }

}
